/* eslint-disable @typescript-eslint/no-unused-vars */
import { Radio } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  getCanOpenInvoiceBillList,
  getInvoiceHistory,
} from '../../../../../api/billing-center/invoice';
import { AutoTable } from '../../../../../components/auto-table';
import { MainButton } from '../../../../../components/button/main';
import { CommonCard } from '../../../../../components/card';
import getFormatMessage from '../../../../../locales/get-format-message';
import { IAPICanOpenInvoiceBillItem } from '../../../../../model/api-modules';
import { IRootState } from '../../../../../store/reducers';
import { GetformatMoney } from '../../billing-overview/component/fomat-money';
import styles from './index.module.scss';
import topStyles from '../index.module.scss';
import {
  getBillColumns,
  formatBillsAsMonth,
  getCanOpenMoney,
  getSubTable,
  MONTH_COLUMNS,
} from './lib';
import { FilterGroup } from '../../../../../components/filter-group';
import DatePicker from 'react-multi-date-picker';
import { CommonRangePicker } from '../../../../../components/date-picker';
import { Moment } from 'moment';
import { getFilterList } from '../lib';
import {
  IBillAsBillItem,
  IBillAsBillOrMonthItem,
} from '../../../../../model/billing-center';
import { CommonRadioButton } from '../../../../../components/radio/radio-button';
import { PermissionCheck } from '../../../permission-check';
import { RbacRoleObjectKey } from '../../../../../model';

interface IProps {
  onApply: (records: IAPICanOpenInvoiceBillItem[]) => void;
  onChange: (money: number) => void;
}

interface IFilter {
  tableType: TableType;
  create_time?: Moment[];
}

enum TableType {
  Bill,
  Month,
}

export const InvoiceBillList = (props: IProps) => {
  const { onChange } = props;
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<ReadonlyArray<IBillAsBillItem>>([]);
  const [selectRows, setSelectRows] = useState<
    ReadonlyArray<IBillAsBillOrMonthItem>
  >([]);
  const [filter, setFilter] = useState<IFilter>({ tableType: TableType.Bill });
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  useEffect(() => {
    init();
  }, [selectStudio]);

  const init = async () => {
    if (!selectStudio?.studioID) {
      return;
    }
    setLoading(true);
    const res = await getCanOpenInvoiceBillList(selectStudio.studioID);
    getInvoiceHistory(selectStudio.studioID);
    onChange(getCanOpenMoney([...res]));
    setList(res.map((item) => ({ ...item, key: item.consume_id })));
    setLoading(false);
  };

  const totalMoney = selectRows.reduce((acc, cur) => {
    const thisMoney = parseFloat(String(cur.amount || 0));
    return acc + thisMoney;
  }, 0);

  const getColumnsConfig = () => {
    const selectRowKeys = selectRows.map((item) => item.key) as string[];

    if (filter.tableType === TableType.Bill) {
      const tableList = [...list].filter(
        (item) => !item['invoice_order.amount']
      );

      return {
        columns: getBillColumns(userInfo),
        list: tableList as IBillAsBillOrMonthItem[],
        rowSelection: {
          selectedRowKeys: selectRowKeys,
          onChange: (
            _selectKeys: readonly (string | number)[],
            selectRows: readonly any[]
          ) => {
            setSelectRows(selectRows);
          },
        },
      };
    }

    const tableList = formatBillsAsMonth(
      getFilterList([...list], { create_time: filter.create_time })
    );

    return {
      columns: MONTH_COLUMNS,
      rowSelection: {
        selectedRowKeys: selectRowKeys,
        onChange: (
          _selectKeys: readonly (string | number)[],
          selectRows: readonly any[]
        ) => {
          const rows = selectRows;
          setSelectRows(rows);
        },
      },
      list: tableList,
    };
  };

  const tableInfo = getColumnsConfig();

  return (
    <CommonCard
      className={styles.billListArea}
      title={getFormatMessage('can_invoice_bills')}
      extra={
        <FilterGroup
          values={filter}
          onFilterChange={(vals, changeField) => {
            if (changeField === 'tableType') {
              setFilter({ tableType: vals.tableType });
              setSelectRows([]);
              return;
            }
            setFilter(vals as IFilter);
          }}
          hiddenFields={
            filter.tableType !== TableType.Month ? ['create_time'] : []
          }
        >
          <CommonRangePicker name="create_time" />

          <CommonRadioButton
            name="tableType"
            optionType="button"
            options={[
              {
                label: getFormatMessage('bill_as_detail'),
                value: TableType.Bill,
              },
              {
                label: getFormatMessage('bill_as_month'),
                value: TableType.Month,
              },
            ]}
          />
        </FilterGroup>
      }
    >
      <AutoTable
        loading={loading}
        list={tableInfo.list}
        tableExtension={{
          onChange: (pagination) => {
            if (!pagination) {
              return;
            }
            setPage(pagination.current);
            setPageSize(pagination.pageSize || 10);
          },
          rowKey: filter.tableType === TableType.Month ? 'key' : 'consume_id',
          expandDetail:
            filter.tableType === TableType.Month
              ? {
                  colSpan: 6,
                  render(record) {
                    const rowItem = record as IBillAsBillOrMonthItem;
                    return getSubTable(rowItem.bills);
                  },
                }
              : undefined,
          rowSelection: tableInfo.rowSelection,
          pagination: {
            current: page,
            total: list.length,
            pageSize,
            showTotal: true,
          },
        }}
        configFields={tableInfo.columns}
      />
      <div className={styles.bottomInfo}>
        <PermissionCheck permissionKey={RbacRoleObjectKey.FeeCenterEdit}>
          <MainButton
            disabled={!selectRows.length}
            onClick={() => {
              if (filter.tableType === TableType.Bill) {
                const rows = [...selectRows] as IAPICanOpenInvoiceBillItem[];
                props.onApply(rows);
                return;
              }

              const bills = selectRows.reduce<IAPICanOpenInvoiceBillItem[]>(
                (acc, row) => {
                  return [...acc, ...(row.bills || [])];
                },
                []
              );
              props.onApply(bills);
            }}
          >
            {getFormatMessage('apply_invoice')}
          </MainButton>
        </PermissionCheck>
        <span>
          <span className={topStyles.moneyLabel}>
            {getFormatMessage('apply_total_invoice_amount')}：
          </span>
          <GetformatMoney
            money={String(totalMoney)}
            className={topStyles.moneyText}
          />
        </span>
      </div>
    </CommonCard>
  );
};
